/**
 * Power Pages tools.
 *
 * Two planes:
 *  - Tier 1 (Dataverse config): list/get/create/update/delete the configuration
 *    records that define a site — web pages, web roles, table permissions, content
 *    snippets, templates, site settings, etc. These run through DataverseApi and
 *    auto-route by the site's data model:
 *      • standard model  -> adx_*  tables  (filter _adx_websiteid_value, bind adx_websiteid)
 *      • enhanced model  -> mspp_* virtual tables (filter _mspp_websiteid_value, bind mspp_websiteid)
 *    "sites" here = the Dataverse website records (adx_website / mspp_website).
 *
 *  - Tier 2 (management/hosting): list/get/create/delete/restart "websites" via the
 *    Power Platform API (PowerPagesApi). "websites" here = the hosted site resources
 *    (status, URL, lifecycle).
 *
 * Entity-set names and powerpagecomponenttype ints verified against Microsoft Learn
 * (mspp_* / powerpagecomponent references). adx_* reference pages are currently 404
 * on Learn; the adx_ names follow the documented Dataverse pluralization + nav-property
 * convention and mirror the confirmed mspp_ tables — see README "Power Pages" notes.
 */

import { z } from "zod";
import { displayValue } from "../utils/format.js";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import { constants as fsConstants } from "node:fs";
import { access, lstat, readFile, realpath } from "node:fs/promises";
import { execFile } from "node:child_process";
import { isIP } from "node:net";
import { basename, dirname, isAbsolute, join, resolve } from "node:path";
import { promisify } from "node:util";
import type { DataverseApi, DataverseRow } from "../api/dataverse-api.js";
import type {
  PowerPagesApi,
  PowerPagesIpAddress,
  PowerPagesOperationResult,
  PowerPagesWafRules,
} from "../api/powerpages-api.js";
import type { PowerPlatformAdminApi } from "../api/power-platform-admin-api.js";
import { ApiError, isPowerAutomateError, ConfirmationRequiredError } from "../utils/errors.js";
import { logger } from "../utils/logger.js";

const execFileAsync = promisify(execFile);

// =============================================================================
// Data-model mapping
// =============================================================================

export type DataModel = "standard" | "enhanced";

interface ComponentMapping {
  /** EntitySetName for the standard (legacy adx_*) data model. */
  adx: string;
  /** EntitySetName for the enhanced (mspp_* virtual) data model. */
  mspp: string;
  /** powerpagecomponenttype int (raw system-table type code; for reference). */
  type: number | null;
  /** Whether this virtual/config table has a direct website lookup. */
  websiteScoped: boolean;
  /** Parent chain used to establish website membership for nested components. */
  parent?: {
    component: string;
    adxLookupField: string;
    msppLookupField: string;
  };
}

/**
 * Component kind -> entity sets per data model. Entity-set names and type ints are
 * verified against the live mspp_* / powerpagecomponent Microsoft Learn references.
 */
const COMPONENT_MAP: Record<string, ComponentMapping> = {
  publishingstate: {
    adx: "adx_publishingstates",
    mspp: "mspp_publishingstates",
    type: 1,
    websiteScoped: true,
  },
  webpage: { adx: "adx_webpages", mspp: "mspp_webpages", type: 2, websiteScoped: true },
  webfile: { adx: "adx_webfiles", mspp: "mspp_webfiles", type: 3, websiteScoped: true },
  weblinkset: { adx: "adx_weblinksets", mspp: "mspp_weblinksets", type: 4, websiteScoped: true },
  weblink: {
    adx: "adx_weblinks",
    mspp: "mspp_weblinks",
    type: 5,
    websiteScoped: false,
    parent: {
      component: "weblinkset",
      adxLookupField: "_adx_weblinksetid_value",
      msppLookupField: "_mspp_weblinksetid_value",
    },
  },
  pagetemplate: {
    adx: "adx_pagetemplates",
    mspp: "mspp_pagetemplates",
    type: 6,
    websiteScoped: true,
  },
  contentsnippet: {
    adx: "adx_contentsnippets",
    mspp: "mspp_contentsnippets",
    type: 7,
    websiteScoped: true,
  },
  webtemplate: { adx: "adx_webtemplates", mspp: "mspp_webtemplates", type: 8, websiteScoped: true },
  sitesetting: { adx: "adx_sitesettings", mspp: "mspp_sitesettings", type: 9, websiteScoped: true },
  webpageaccesscontrolrule: {
    adx: "adx_webpageaccesscontrolrules",
    mspp: "mspp_webpageaccesscontrolrules",
    type: 10,
    websiteScoped: true,
  },
  webrole: { adx: "adx_webroles", mspp: "mspp_webroles", type: 11, websiteScoped: true },
  websiteaccess: {
    adx: "adx_websiteaccesses",
    mspp: "mspp_websiteaccesses",
    type: 12,
    websiteScoped: true,
  },
  sitemarker: { adx: "adx_sitemarkers", mspp: "mspp_sitemarkers", type: 13, websiteScoped: true },
  basicform: { adx: "adx_entityforms", mspp: "mspp_entityforms", type: 15, websiteScoped: true },
  basicformmetadata: {
    adx: "adx_entityformmetadatas",
    mspp: "mspp_entityformmetadatas",
    type: 16,
    websiteScoped: false,
    parent: {
      component: "basicform",
      adxLookupField: "_adx_entityform_value",
      msppLookupField: "_mspp_entityform_value",
    },
  },
  list: { adx: "adx_entitylists", mspp: "mspp_entitylists", type: 17, websiteScoped: true },
  tablepermission: {
    adx: "adx_entitypermissions",
    mspp: "mspp_entitypermissions",
    type: 18,
    websiteScoped: true,
  },
  advancedform: { adx: "adx_webforms", mspp: "mspp_webforms", type: 19, websiteScoped: true },
  advancedformstep: {
    adx: "adx_webformsteps",
    mspp: "mspp_webformsteps",
    type: 20,
    websiteScoped: false,
    parent: {
      component: "advancedform",
      adxLookupField: "_adx_webform_value",
      msppLookupField: "_mspp_webform_value",
    },
  },
  advancedformmetadata: {
    adx: "adx_webformmetadatas",
    mspp: "mspp_webformmetadatas",
    type: 21,
    websiteScoped: false,
    parent: {
      component: "advancedformstep",
      adxLookupField: "_adx_webformstep_value",
      msppLookupField: "_mspp_webformstep_value",
    },
  },
  pollplacement: {
    adx: "adx_pollplacements",
    mspp: "mspp_pollplacements",
    type: 24,
    websiteScoped: true,
  },
  columnpermissionprofile: {
    adx: "adx_columnpermissionprofiles",
    mspp: "mspp_columnpermissionprofiles",
    type: 28,
    websiteScoped: true,
  },
  columnpermission: {
    adx: "adx_columnpermissions",
    mspp: "mspp_columnpermissions",
    type: 29,
    websiteScoped: false,
    parent: {
      component: "columnpermissionprofile",
      adxLookupField: "_adx_columnpermissionprofileid_value",
      msppLookupField: "_mspp_columnpermissionprofileid_value",
    },
  },
  redirect: { adx: "adx_redirects", mspp: "mspp_redirects", type: 30, websiteScoped: true },
  publishingstatetransition: {
    adx: "adx_publishingstatetransitionrules",
    mspp: "mspp_publishingstatetransitionrules",
    type: null,
    websiteScoped: true,
  },
  shortcut: { adx: "adx_shortcuts", mspp: "mspp_shortcuts", type: null, websiteScoped: true },
  websitelanguage: {
    adx: "adx_websitelanguages",
    mspp: "mspp_websitelanguages",
    type: null,
    websiteScoped: true,
  },
};

const COMPONENT_KEYS = Object.keys(COMPONENT_MAP) as [string, ...string[]];
const COMPONENT_LIST = COMPONENT_KEYS.join(", ");

const RELATIONSHIP_MAP = {
  tablepermission_webrole: {
    sourceComponent: "tablepermission",
    adx: "adx_entitypermission_webrole",
    mspp: "mspp_entitypermission_webrole",
  },
  pageaccessrule_webrole: {
    sourceComponent: "webpageaccesscontrolrule",
    adx: "adx_webpageaccesscontrolrule_webrole",
    mspp: "mspp_webpageaccesscontrolrule_webrole",
  },
  publishingtransition_webrole: {
    sourceComponent: "publishingstatetransition",
    adx: "adx_publishingstatetransitionrule_webrole",
    mspp: "mspp_publishingstatetransitionrule_webrole",
  },
  columnpermissionprofile_webrole: {
    sourceComponent: "columnpermissionprofile",
    adx: "adx_columnpermissionprofile_webrole",
    mspp: "mspp_columnpermissionprofile_webrole",
  },
  websiteaccess_webrole: {
    sourceComponent: "websiteaccess",
    adx: "adx_websiteaccess_webrole",
    mspp: "mspp_websiteaccess_webrole",
  },
} as const;

const RELATIONSHIP_KEYS = Object.keys(RELATIONSHIP_MAP) as [
  keyof typeof RELATIONSHIP_MAP,
  ...(keyof typeof RELATIONSHIP_MAP)[],
];

// Website root tables (used for listing sites + detecting the data model).
const ENHANCED_SITE_SET = "mspp_websites";
const STANDARD_SITE_SET = "adx_websites";

interface ResolvedTarget {
  model: DataModel;
  entitySet: string;
  /** OData filter VALUE field for the website lookup. */
  websiteFilterField: string;
  /** @odata.bind navigation property for associating with the website. */
  websiteBindProp: string;
  /** Collection the bind points at, e.g. "/adx_websites". */
  websiteBindCollection: string;
  websiteScoped: boolean;
}

function targetFor(model: DataModel, component: string): ResolvedTarget {
  const mapping = COMPONENT_MAP[component];
  if (!mapping) {
    throw new ApiError(
      `Unknown Power Pages component "${component}". Valid: ${COMPONENT_LIST}`,
      400,
    );
  }
  if (model === "enhanced") {
    return {
      model,
      entitySet: mapping.mspp,
      websiteFilterField: "_mspp_websiteid_value",
      websiteBindProp: "mspp_websiteid@odata.bind",
      websiteBindCollection: `/${ENHANCED_SITE_SET}`,
      websiteScoped: mapping.websiteScoped,
    };
  }
  return {
    model,
    entitySet: mapping.adx,
    websiteFilterField: "_adx_websiteid_value",
    websiteBindProp: "adx_websiteid@odata.bind",
    websiteBindCollection: `/${STANDARD_SITE_SET}`,
    websiteScoped: mapping.websiteScoped,
  };
}

// =============================================================================
// Helpers
// =============================================================================

function dataverseUnavailable(): string {
  const initError = (globalThis as Record<string, unknown>).__dataverseInitError;
  const reason = initError ? `\n\nInit error: ${displayValue(initError)}` : "";
  return `Dataverse is not available for this environment.

Power Pages configuration (sites, pages, web roles, etc.) lives in Dataverse, so these
tools require a Dataverse-enabled environment. The default environment may not have a
Dataverse database provisioned.${reason}`;
}

function is404(err: unknown): boolean {
  return isPowerAutomateError(err) && err.statusCode === 404;
}

/**
 * The mspp_* tables are VIRTUAL — their data provider throws
 * "Value cannot be null. Parameter name: entityMetadata" (0x0) on some
 * tenants instead of a clean 404/miss, most reliably on retrieve-by-key
 * (field report 2026-08-07: every site/component tool failed identically
 * on two unrelated sites while retrieveMultiple-based list_powerpages_sites
 * worked). Treat it as "this model is not usable here", never as fatal.
 */
function isEntityMetadataNull(err: unknown): boolean {
  return err instanceof Error && /entityMetadata/i.test(err.message);
}

/**
 * Does the site exist in the given model? Probes with retrieveMultiple +
 * $filter — the operation the virtual provider handles reliably — instead of
 * retrieve-by-key. "provider-error" = the model's tables errored structurally
 * (entityMetadata null), which callers treat as a miss but may report.
 */
async function probeSiteInModel(
  api: DataverseApi,
  model: DataModel,
  siteId: string,
): Promise<"hit" | "miss" | "provider-error"> {
  const entitySet = model === "enhanced" ? ENHANCED_SITE_SET : STANDARD_SITE_SET;
  const idColumn = model === "enhanced" ? "mspp_websiteid" : "adx_websiteid";
  try {
    const result = await api.queryRows(entitySet, {
      select: idColumn,
      filter: `${idColumn} eq ${siteId}`,
      top: 1,
    });
    return result.value.length > 0 ? "hit" : "miss";
  } catch (err) {
    if (is404(err)) return "miss";
    if (isEntityMetadataNull(err)) return "provider-error";
    throw err;
  }
}

function formatRow(row: DataverseRow, indent = "    "): string {
  const lines: string[] = [];
  for (const [key, value] of Object.entries(row)) {
    if (key.startsWith("@odata")) continue;
    if (key.startsWith("_") && key.endsWith("_value")) continue;
    if (value === null || value === undefined) continue;
    lines.push(`${indent}${key}: ${displayValue(value)}`);
  }
  return lines.join("\n");
}

/**
 * Detect a site's data model from its website record id.
 * Enhanced sites have a row in mspp_websites; standard sites in adx_websites.
 * Non-404 errors (network/auth) are propagated so real failures surface.
 */
async function resolveModel(api: DataverseApi, siteId: string): Promise<DataModel> {
  const enhanced = await probeSiteInModel(api, "enhanced", siteId);
  if (enhanced === "hit") return "enhanced";
  const standard = await probeSiteInModel(api, "standard", siteId);
  if (standard === "hit") return "standard";

  const providerNote =
    enhanced === "provider-error" || standard === "provider-error"
      ? " Note: the environment's Power Pages virtual tables (mspp_*) returned a structural error " +
        '("Value cannot be null: entityMetadata") — the site may exist but its metadata provider is ' +
        "not answering; retry, or check the site in the Power Pages design studio."
      : "";
  throw new ApiError(
    `No Power Pages site found with id ${siteId} in either the enhanced (mspp_websites) or standard (adx_websites) model. Use list_powerpages_sites to find valid site ids.${providerNote}`,
    404,
  );
}

async function resolveModelOrExplicit(
  api: DataverseApi,
  siteId: string,
  explicit?: DataModel,
): Promise<DataModel> {
  return explicit ?? resolveModel(api, siteId);
}

/** Mutations must prove the site exists in the selected model; an explicit hint is not trust. */
async function resolveMutationModel(
  api: DataverseApi,
  siteId: string,
  explicit?: DataModel,
): Promise<DataModel> {
  if (explicit) {
    // An explicit model is a routing hint, not a trust bypass: prove the site
    // exists in that model before touching the component. This intentionally
    // avoids probing the other model, whose tables/licensing may be absent in a
    // partially provisioned tenant. Probed via $filter, not retrieve-by-key —
    // the mspp_* virtual provider errors on by-key retrieves on some tenants.
    const probe = await probeSiteInModel(api, explicit, siteId);
    if (probe !== "hit") {
      throw new ApiError(
        `No Power Pages site with id ${siteId} in the ${explicit} model` +
          (probe === "provider-error"
            ? ' (the model\'s virtual tables returned "Value cannot be null: entityMetadata")'
            : "") +
          ". Use list_powerpages_sites to confirm the site's model.",
        404,
      );
    }
    return explicit;
  }
  return resolveModel(api, siteId);
}

// =============================================================================
// Zod input schemas
// =============================================================================

const modelEnum = z.enum(["standard", "enhanced"]).optional();
const componentEnum = z.enum(COMPONENT_KEYS);
// Strict GUID. These ids are interpolated into OData filters and @odata.bind values, so
// they MUST be validated at the boundary — validateODataFilter only blocks structural
// tautologies, not value-level injection, and the explicit-`model` path skips the
// getRow()/validateRowId() check. Mirrors validateRowId() in src/utils/security.ts.
const guid = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "must be a GUID");

const listSitesSchema = z.object({ top: z.number().min(1).max(250).optional() });
const getSiteSchema = z.object({ siteId: guid });

const listComponentsSchema = z.object({
  siteId: guid,
  component: componentEnum,
  model: modelEnum,
  select: z.string().optional(),
  filter: z.string().optional(),
  orderBy: z.string().optional(),
  top: z.number().min(1).max(250).optional(),
});
const getComponentSchema = z.object({
  siteId: guid,
  component: componentEnum,
  id: guid,
  model: modelEnum,
  select: z.string().optional(),
});
const createComponentSchema = z.object({
  siteId: guid,
  component: componentEnum,
  data: z.record(z.string(), z.unknown()),
  model: modelEnum,
});
const updateComponentSchema = z.object({
  siteId: guid,
  component: componentEnum,
  id: guid,
  data: z.record(z.string(), z.unknown()),
  model: modelEnum,
});
const deleteComponentSchema = z.object({
  siteId: guid,
  component: componentEnum,
  id: guid,
  model: modelEnum,
  confirm: z.boolean().optional(),
});
const relationshipSchema = z.object({
  siteId: guid,
  relationship: z.enum(RELATIONSHIP_KEYS),
  sourceId: guid,
  webRoleId: guid,
  action: z.enum(["associate", "disassociate"]),
  model: modelEnum,
  confirm: z.boolean().optional(),
});
const uploadWebFileContentSchema = z.object({
  siteId: guid,
  webFileId: guid,
  filePath: z.string().min(1),
  fileName: z.string().min(1).max(255).optional(),
  mimeType: z.string().min(1).max(255).optional(),
  model: modelEnum,
  confirm: z.boolean().optional(),
});

// Management (Tier 2)
const listWebsitesSchema = z.object({ environment: z.string().optional() });
const getWebsiteSchema = z.object({ websiteId: guid, environment: z.string().optional() });
const createWebsiteSchema = z.object({
  name: z.string().min(1).max(200),
  subdomain: z.string().min(1).max(63),
  templateName: z
    .enum(["DefaultPortalTemplate", "PowerPortals_ProgramRegistration", "PowerPortals_BookMeeting"])
    .optional(),
  languageLcid: z.number().int().optional(),
  dataverseOrganizationId: guid.optional(),
  websiteRecordId: guid.optional(),
  environment: z.string().optional(),
  waitForCompletion: z.boolean().optional(),
  timeoutSeconds: z.number().int().min(1).max(600).optional(),
  confirm: z.boolean().optional(),
});
const deleteWebsiteSchema = z.object({
  websiteId: guid,
  environment: z.string().optional(),
  waitForCompletion: z.boolean().optional(),
  timeoutSeconds: z.number().int().min(1).max(600).optional(),
  confirm: z.boolean().optional(),
});
const restartWebsiteSchema = z.object({ websiteId: guid, environment: z.string().optional() });

const websiteManagementSchema = z.object({
  websiteId: guid,
  environment: z.string().optional(),
});
const confirmedWebsiteManagementSchema = websiteManagementSchema.extend({
  confirm: z.boolean().optional(),
  waitForCompletion: z.boolean().optional(),
  timeoutSeconds: z.number().int().min(1).max(600).optional(),
});
const operationStatusSchema = z.object({
  operationLocation: z.string().url(),
  waitForCompletion: z.boolean().optional(),
  timeoutSeconds: z.number().int().min(1).max(600).optional(),
});

function parseIpAddress(value: string): PowerPagesIpAddress {
  const [address, prefix, extra] = value.split("/");
  if (!address || extra !== undefined)
    throw new ApiError(`Invalid IP address or CIDR: ${value}`, 400);
  const version = isIP(address);
  if (version === 0) throw new ApiError(`Invalid IP address or CIDR: ${value}`, 400);
  if (prefix !== undefined) {
    const parsedPrefix = Number(prefix);
    const max = version === 4 ? 32 : 128;
    if (!Number.isInteger(parsedPrefix) || parsedPrefix < 0 || parsedPrefix > max) {
      throw new ApiError(`Invalid CIDR prefix in ${value}`, 400);
    }
  }
  return { IpAddress: value, IpAddressType: version === 4 ? "IPv4" : "IPv6" };
}

const ipMutationSchema = confirmedWebsiteManagementSchema.extend({
  ipAddresses: z.array(z.string().min(2).max(64)).min(1).max(200),
});
const hostnameSchema = confirmedWebsiteManagementSchema.extend({
  hostName: z.string().min(4).max(253),
});
const customDomainCreateSchema = confirmedWebsiteManagementSchema.extend({
  customDomain: z.string().min(4).max(253),
});
const certificateListSchema = websiteManagementSchema.extend({
  certType: z.enum(["SSL", "MANAGED"]),
});
const certificateUploadSchema = confirmedWebsiteManagementSchema
  .extend({
    certType: z.enum(["SSL", "MANAGED"]),
    certificatePath: z.string().min(1),
    passwordEnvironmentVariable: z
      .string()
      .regex(/^[A-Za-z_][A-Za-z0-9_]*$/)
      .optional(),
    passwordless: z.boolean().optional(),
  })
  .refine((value) => Boolean(value.passwordEnvironmentVariable) !== (value.passwordless === true), {
    message: "Provide exactly one of passwordEnvironmentVariable or passwordless=true",
  });
const certificateDeleteSchema = confirmedWebsiteManagementSchema.extend({
  certType: z.enum(["SSL", "MANAGED"]),
  thumbprint: z.string().min(40).max(128),
});
const sslBindingSchema = confirmedWebsiteManagementSchema.extend({
  hostName: z.string().min(4).max(253),
  thumbprint: z.string().min(40).max(128),
});
const sslBindingListSchema = websiteManagementSchema.extend({
  hostName: z.string().min(4).max(253),
});
const wafGetRulesSchema = websiteManagementSchema.extend({
  ruleType: z.string().max(100).optional(),
});
const wafRulesMutationSchema = confirmedWebsiteManagementSchema.extend({
  customRules: z.array(z.record(z.string(), z.unknown())).max(100).optional(),
  managedRules: z.array(z.record(z.string(), z.unknown())).max(100).optional(),
});
const wafDeleteRulesSchema = confirmedWebsiteManagementSchema.extend({
  ruleNames: z.array(z.string().min(1).max(128)).min(1).max(100),
});
const quickScanSchema = confirmedWebsiteManagementSchema.extend({
  lcid: z
    .string()
    .regex(/^\d{1,10}$/)
    .optional(),
});
const trialConversionSchema = confirmedWebsiteManagementSchema.extend({
  enableCdn: z.boolean().optional(),
  enableWaf: z.boolean().optional(),
  useDynamics365PortalAddOnLicense: z.boolean().optional(),
});
const setDataModelSchema = confirmedWebsiteManagementSchema.extend({
  enhanced: z.boolean(),
});
const toggleAfdSchema = confirmedWebsiteManagementSchema.extend({ enabled: z.boolean() });
const securityGroupSchema = confirmedWebsiteManagementSchema
  .extend({ securityGroupId: guid.optional(), clear: z.boolean().optional() })
  .refine((value) => Boolean(value.securityGroupId) !== Boolean(value.clear), {
    message: "Provide exactly one of securityGroupId or clear=true",
  });
const visibilitySchema = confirmedWebsiteManagementSchema.extend({
  visibility: z.enum(["public", "private"]),
});

const pacEnvironment = z.string().min(1).max(500).optional();
const pacModelVersion = z.enum(["Enhanced", "Standard"]);
const pacConfirmation = z.object({ confirm: z.boolean().optional() });
const pacBootstrapSchema = pacConfirmation.extend({ path: z.string().min(1) });
const pacCloneSchema = pacConfirmation.extend({
  path: z.string().min(1),
  outputDirectory: z.string().min(1),
  name: z.string().min(1).max(200).optional(),
  overwrite: z.boolean().optional(),
});
const pacDownloadSchema = pacConfirmation.extend({
  path: z.string().min(1),
  websiteId: guid,
  environment: pacEnvironment,
  excludeEntities: z
    .array(z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/))
    .max(100)
    .optional(),
  includeEntities: z
    .array(z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/))
    .max(100)
    .optional(),
  modelVersion: pacModelVersion.optional(),
  overwrite: z.boolean().optional(),
});
const pacCodeDownloadSchema = pacConfirmation.extend({
  path: z.string().min(1),
  websiteId: guid,
  environment: pacEnvironment,
  overwrite: z.boolean().optional(),
});
const pacListSchema = z.object({ environment: pacEnvironment, verbose: z.boolean().optional() });
const pacMigrateSchema = pacConfirmation.extend({
  websiteId: guid,
  environment: pacEnvironment,
  checkMigrationStatus: z.boolean().optional(),
  mode: z.enum(["configurationData", "configurationDataReferences", "all"]).optional(),
  portalId: guid.optional(),
  resetMigration: z.boolean().optional(),
  revertToStandardDataModel: z.boolean().optional(),
  siteCustomizationReportPath: z.string().min(1).optional(),
  updateDataModelVersion: z.boolean().optional(),
  verbose: z.boolean().optional(),
});
const pacUploadSchema = pacConfirmation.extend({
  path: z.string().min(1),
  environment: pacEnvironment,
  deploymentProfile: z
    .string()
    .regex(/^[A-Za-z0-9_.-]{1,100}$/)
    .optional(),
  forceUploadAll: z.boolean().optional(),
  modelVersion: pacModelVersion.optional(),
});
const pacCodeUploadSchema = pacConfirmation.extend({
  rootPath: z.string().min(1),
  compiledPath: z.string().min(1).optional(),
  siteName: z.string().min(1).max(200).optional(),
});

// =============================================================================
// Tool definitions
// =============================================================================

const componentDesc = `Power Pages component kind. One of: ${COMPONENT_LIST}.`;

export const listPowerPagesSitesTool: Tool = {
  name: "list_powerpages_sites",
  description:
    "List Power Pages sites as stored in Dataverse (the configuration plane). Returns enhanced-model sites (mspp_websites) and standard-model sites (adx_websites), each tagged with its data model and the site id to pass to the component tools. For hosting/status/URL use list_powerpages_websites instead.",
  inputSchema: {
    type: "object",
    properties: {
      top: {
        type: "number",
        description: "Max sites per model to return (default 50, max 250)",
        minimum: 1,
        maximum: 250,
      },
    },
  },
};

export const getPowerPagesSiteTool: Tool = {
  name: "get_powerpages_site",
  description:
    "Get a Power Pages site's Dataverse record and detected data model (standard vs enhanced) by its site id (from list_powerpages_sites).",
  inputSchema: {
    type: "object",
    properties: {
      siteId: {
        type: "string",
        description: "Site id (mspp_websiteid for enhanced, adx_websiteid for standard)",
      },
    },
    required: ["siteId"],
  },
};

export const listPowerPagesComponentsTool: Tool = {
  name: "list_powerpages_components",
  description:
    "List configuration components of a Power Pages site across the standard and enhanced data models. Directly site-scoped types are restricted to siteId automatically. Nested types (weblink, form metadata/steps, columnpermission) require an explicit parent OData filter.",
  inputSchema: {
    type: "object",
    properties: {
      siteId: { type: "string", description: "Site id from list_powerpages_sites" },
      component: { type: "string", enum: COMPONENT_KEYS, description: componentDesc },
      model: {
        type: "string",
        enum: ["standard", "enhanced"],
        description: "Override data-model detection (optional)",
      },
      select: { type: "string", description: "Comma-separated columns to return (optional)" },
      filter: {
        type: "string",
        description: "Extra OData filter, ANDed with the site filter (optional)",
      },
      orderBy: { type: "string", description: "OData order by (optional)" },
      top: {
        type: "number",
        description: "Max rows (default 50, max 250)",
        minimum: 1,
        maximum: 250,
      },
    },
    required: ["siteId", "component"],
  },
};

export const getPowerPagesComponentTool: Tool = {
  name: "get_powerpages_component",
  description:
    "Get a single Power Pages configuration component row by its record id. siteId selects the data model.",
  inputSchema: {
    type: "object",
    properties: {
      siteId: { type: "string", description: "Site id (selects the data model)" },
      component: { type: "string", enum: COMPONENT_KEYS, description: componentDesc },
      id: { type: "string", description: "The component record id (GUID)" },
      model: {
        type: "string",
        enum: ["standard", "enhanced"],
        description: "Override data-model detection (optional)",
      },
      select: { type: "string", description: "Comma-separated columns to return (optional)" },
    },
    required: ["siteId", "component", "id"],
  },
};

export const createPowerPagesComponentTool: Tool = {
  name: "create_powerpages_component",
  description:
    "Create a Power Pages configuration component. Directly site-scoped types receive the website @odata.bind automatically. Nested types require their parent @odata.bind in data. Use upload_powerpages_webfile_content for standard-model web-file bytes.",
  inputSchema: {
    type: "object",
    properties: {
      siteId: { type: "string", description: "Site id the component belongs to" },
      component: { type: "string", enum: COMPONENT_KEYS, description: componentDesc },
      data: {
        type: "object",
        description: "Column values for the new row",
        additionalProperties: true,
      },
      model: {
        type: "string",
        enum: ["standard", "enhanced"],
        description: "Override data-model detection (optional)",
      },
    },
    required: ["siteId", "component", "data"],
  },
};

export const updatePowerPagesComponentTool: Tool = {
  name: "update_powerpages_component",
  description:
    "Update a Power Pages configuration component row. Only include columns you want to change in `data`.",
  inputSchema: {
    type: "object",
    properties: {
      siteId: { type: "string", description: "Site id (selects the data model)" },
      component: { type: "string", enum: COMPONENT_KEYS, description: componentDesc },
      id: { type: "string", description: "The component record id (GUID)" },
      data: { type: "object", description: "Column values to update", additionalProperties: true },
      model: {
        type: "string",
        enum: ["standard", "enhanced"],
        description: "Override data-model detection (optional)",
      },
    },
    required: ["siteId", "component", "id", "data"],
  },
};

export const deletePowerPagesComponentTool: Tool = {
  name: "delete_powerpages_component",
  description:
    "Delete a Power Pages configuration component row permanently. Set confirm=true to proceed.",
  inputSchema: {
    type: "object",
    properties: {
      siteId: { type: "string", description: "Site id (selects the data model)" },
      component: { type: "string", enum: COMPONENT_KEYS, description: componentDesc },
      id: { type: "string", description: "The component record id (GUID)" },
      model: {
        type: "string",
        enum: ["standard", "enhanced"],
        description: "Override data-model detection (optional)",
      },
      confirm: { type: "boolean", description: "Set to true to confirm deletion" },
    },
    required: ["siteId", "component", "id"],
  },
};

export const managePowerPagesRelationshipTool: Tool = {
  name: "manage_powerpages_relationship",
  description:
    "Associate or disassociate a Power Pages security/configuration record with a web role using a documented Dataverse relationship. Both records are verified to belong to siteId. Set confirm=true because these relationships affect authorization.",
  inputSchema: {
    type: "object",
    properties: {
      siteId: { type: "string", description: "Power Pages Dataverse site GUID" },
      relationship: {
        type: "string",
        enum: RELATIONSHIP_KEYS,
        description: "Whitelisted Power Pages relationship",
      },
      sourceId: { type: "string", description: "Permission/rule/profile record GUID" },
      webRoleId: { type: "string", description: "Web role GUID" },
      action: { type: "string", enum: ["associate", "disassociate"] },
      model: { type: "string", enum: ["standard", "enhanced"] },
      confirm: { type: "boolean", description: "Set true to apply the authorization change" },
    },
    required: ["siteId", "relationship", "sourceId", "webRoleId", "action"],
  },
};

export const uploadPowerPagesWebFileContentTool: Tool = {
  name: "upload_powerpages_webfile_content",
  description:
    "Attach local file bytes to a standard-model Power Pages web-file record using the documented newest-note attachment format. Reads an absolute regular local path (max 15 MiB); does not place base64 in the MCP request. Enhanced virtual-table sites must use pac_pages_upload. Set confirm=true.",
  inputSchema: {
    type: "object",
    properties: {
      siteId: { type: "string", description: "Power Pages Dataverse site GUID" },
      webFileId: { type: "string", description: "adx_webfile GUID" },
      filePath: { type: "string", description: "Absolute, normalized local file path" },
      fileName: { type: "string", description: "Attachment filename; defaults to basename" },
      mimeType: { type: "string", description: "MIME type; defaults to application/octet-stream" },
      model: { type: "string", enum: ["standard", "enhanced"] },
      confirm: { type: "boolean", description: "Set true to upload bytes" },
    },
    required: ["siteId", "webFileId", "filePath"],
  },
};

export const listPowerPagesWebsitesTool: Tool = {
  name: "list_powerpages_websites",
  description:
    "List Power Pages websites in an environment via the Power Platform management API. Returns hosting info: status, public URL, data model, and type (production/trial). For the Dataverse config behind a site use list_powerpages_sites.",
  inputSchema: {
    type: "object",
    properties: {
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
    },
  },
};

export const getPowerPagesWebsiteTool: Tool = {
  name: "get_powerpages_website",
  description:
    "Get a Power Pages website's hosting details (status, URL, data model) by id, via the management API.",
  inputSchema: {
    type: "object",
    properties: {
      websiteId: { type: "string", description: "The website id (GUID)" },
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
    },
    required: ["websiteId"],
  },
};

export const createPowerPagesWebsiteTool: Tool = {
  name: "create_powerpages_website",
  description:
    "Provision a new Power Pages website (management API). Asynchronous — returns a tracking URL; provisioning takes several minutes. The Dataverse org id is resolved from the environment if not supplied. Set confirm=true to proceed.",
  inputSchema: {
    type: "object",
    properties: {
      name: { type: "string", description: "Display name for the site" },
      subdomain: {
        type: "string",
        description: "Subdomain (lowercase letters, digits, hyphens), e.g. 'contoso-help'",
      },
      templateName: {
        type: "string",
        enum: [
          "DefaultPortalTemplate",
          "PowerPortals_ProgramRegistration",
          "PowerPortals_BookMeeting",
        ],
        description: "Site template (default: DefaultPortalTemplate)",
      },
      languageLcid: {
        type: "number",
        description: "Base language LCID (default 1033 = English US)",
      },
      dataverseOrganizationId: {
        type: "string",
        description: "Dataverse org GUID. Auto-resolved from the environment if omitted.",
      },
      websiteRecordId: {
        type: "string",
        description: "Optional existing Dataverse website record GUID to bind during provisioning",
      },
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
      waitForCompletion: { type: "boolean", description: "Poll Operation-Location until terminal" },
      timeoutSeconds: {
        type: "number",
        minimum: 1,
        maximum: 600,
        description: "Polling timeout (default 60 seconds)",
      },
      confirm: { type: "boolean", description: "Set to true to confirm provisioning" },
    },
    required: ["name", "subdomain"],
  },
};

export const deletePowerPagesWebsiteTool: Tool = {
  name: "delete_powerpages_website",
  description:
    "Delete a Power Pages website (management API). This cannot be undone. Set confirm=true to proceed.",
  inputSchema: {
    type: "object",
    properties: {
      websiteId: { type: "string", description: "The website id (GUID) to delete" },
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
      waitForCompletion: { type: "boolean", description: "Poll Operation-Location until terminal" },
      timeoutSeconds: {
        type: "number",
        minimum: 1,
        maximum: 600,
        description: "Polling timeout (default 60 seconds)",
      },
      confirm: { type: "boolean", description: "Set to true to confirm deletion" },
    },
    required: ["websiteId"],
  },
};

export const restartPowerPagesWebsiteTool: Tool = {
  name: "restart_powerpages_website",
  description:
    "Restart a Power Pages website (management API). Recycles the site so it picks up Dataverse config changes — the programmatic equivalent of the studio 'Sync'. Synchronous.",
  inputSchema: {
    type: "object",
    properties: {
      websiteId: { type: "string", description: "The website id (GUID)" },
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
    },
    required: ["websiteId"],
  },
};

type JsonSchemaProperty = Record<string, unknown>;
const websiteIdProperty: JsonSchemaProperty = {
  type: "string",
  description: "Power Pages management website GUID",
};
const environmentProperty: JsonSchemaProperty = {
  type: "string",
  description: "Environment GUID; uses the configured default when omitted",
};
const confirmationProperty: JsonSchemaProperty = {
  type: "boolean",
  description: "Must be true to apply this change",
};
const waitProperty: JsonSchemaProperty = {
  type: "boolean",
  description: "Poll Operation-Location until terminal when the service runs asynchronously",
};
const timeoutProperty: JsonSchemaProperty = {
  type: "number",
  minimum: 1,
  maximum: 600,
  description: "Polling timeout in seconds (default 60, max 600)",
};

function managementTool(
  name: string,
  description: string,
  options: {
    properties?: Record<string, JsonSchemaProperty>;
    required?: string[];
    mutating?: boolean;
    destructive?: boolean;
    includeWebsite?: boolean;
  } = {},
): Tool {
  const mutating = options.mutating === true;
  const properties: Record<string, JsonSchemaProperty> = {
    ...(options.includeWebsite === false
      ? {}
      : { websiteId: websiteIdProperty, environment: environmentProperty }),
    ...(options.properties ?? {}),
    ...(mutating
      ? {
          confirm: confirmationProperty,
          waitForCompletion: waitProperty,
          timeoutSeconds: timeoutProperty,
        }
      : {}),
  };
  return {
    name,
    description,
    inputSchema: {
      type: "object",
      properties,
      required: [
        ...(options.includeWebsite === false ? [] : ["websiteId"]),
        ...(options.required ?? []),
      ],
      additionalProperties: false,
    },
    ...(options.destructive ? { annotations: { destructiveHint: true } } : {}),
  };
}

export const additionalPowerPagesManagementTools: Tool[] = [
  managementTool(
    "get_powerpages_operation_status",
    "Get or boundedly wait for a Power Pages Operation-Location result. Only authenticated HTTPS URLs on api.powerplatform.com are accepted.",
    {
      includeWebsite: false,
      properties: {
        operationLocation: {
          type: "string",
          description: "Operation-Location URL returned by a Power Pages write",
        },
        waitForCompletion: waitProperty,
        timeoutSeconds: timeoutProperty,
      },
      required: ["operationLocation"],
    },
  ),
  managementTool("get_powerpages_allowed_ip_addresses", "Get the website IP allow list."),
  managementTool(
    "add_powerpages_allowed_ip_addresses",
    "Add IPv4/IPv6 addresses or CIDR ranges to the website allow list. Set confirm=true.",
    {
      mutating: true,
      properties: {
        ipAddresses: { type: "array", minItems: 1, maxItems: 200, items: { type: "string" } },
      },
      required: ["ipAddresses"],
    },
  ),
  managementTool(
    "remove_powerpages_allowed_ip_addresses",
    "Remove IPv4/IPv6 addresses or CIDR ranges from the website allow list. Set confirm=true.",
    {
      mutating: true,
      properties: {
        ipAddresses: { type: "array", minItems: 1, maxItems: 200, items: { type: "string" } },
      },
      required: ["ipAddresses"],
    },
  ),
  managementTool("list_powerpages_custom_domains", "List custom domains configured for a website."),
  managementTool(
    "create_powerpages_custom_domain",
    "Add a custom domain to a website. DNS validation still applies. Set confirm=true.",
    {
      mutating: true,
      properties: {
        customDomain: { type: "string", description: "Fully qualified custom domain" },
      },
      required: ["customDomain"],
    },
  ),
  managementTool(
    "delete_powerpages_custom_domain",
    "Remove a custom domain from a website. Set confirm=true.",
    {
      mutating: true,
      destructive: true,
      properties: { hostName: { type: "string" } },
      required: ["hostName"],
    },
  ),
  managementTool(
    "list_powerpages_certificates",
    "List SSL or managed certificates for a website.",
    {
      properties: { certType: { type: "string", enum: ["SSL", "MANAGED"] } },
      required: ["certType"],
    },
  ),
  managementTool(
    "upload_powerpages_certificate",
    "Upload a local PFX certificate using multipart form data. Reads an absolute canonical path (max 16 MiB); password may come from a named environment variable. Set confirm=true.",
    {
      mutating: true,
      properties: {
        certType: { type: "string", enum: ["SSL", "MANAGED"] },
        certificatePath: { type: "string", description: "Absolute canonical local .pfx path" },
        passwordEnvironmentVariable: {
          type: "string",
          description: "Optional environment variable holding the PFX password",
        },
        passwordless: {
          type: "boolean",
          description: "Set true only when the PFX password is the empty string",
        },
      },
      required: ["certType", "certificatePath"],
    },
  ),
  managementTool(
    "delete_powerpages_certificate",
    "Delete a certificate by thumbprint and type. Set confirm=true.",
    {
      mutating: true,
      destructive: true,
      properties: {
        certType: { type: "string", enum: ["SSL", "MANAGED"] },
        thumbprint: { type: "string" },
      },
      required: ["certType", "thumbprint"],
    },
  ),
  managementTool("list_powerpages_ssl_bindings", "List SSL bindings for a custom hostname.", {
    properties: { hostName: { type: "string" } },
    required: ["hostName"],
  }),
  managementTool(
    "add_powerpages_ssl_binding",
    "Bind a certificate thumbprint to a custom hostname. Set confirm=true.",
    {
      mutating: true,
      properties: { hostName: { type: "string" }, thumbprint: { type: "string" } },
      required: ["hostName", "thumbprint"],
    },
  ),
  managementTool(
    "delete_powerpages_ssl_binding",
    "Delete an SSL binding by hostname and thumbprint. Set confirm=true.",
    {
      mutating: true,
      destructive: true,
      properties: { hostName: { type: "string" }, thumbprint: { type: "string" } },
      required: ["hostName", "thumbprint"],
    },
  ),
  managementTool("get_powerpages_waf_status", "Get the website web application firewall status."),
  managementTool(
    "get_powerpages_waf_rules",
    "Get managed and custom web application firewall rules.",
    {
      properties: {
        ruleType: { type: "string", description: "Optional platform rule-type filter" },
      },
    },
  ),
  managementTool(
    "enable_powerpages_waf",
    "Enable the website web application firewall. Set confirm=true.",
    { mutating: true },
  ),
  managementTool(
    "disable_powerpages_waf",
    "Disable the website web application firewall. Set confirm=true.",
    { mutating: true },
  ),
  managementTool(
    "create_powerpages_waf_rules",
    "Create or update managed/custom WAF rules using the published 2024-10-01 contract. Set confirm=true.",
    {
      mutating: true,
      properties: {
        customRules: { type: "array", items: { type: "object", additionalProperties: true } },
        managedRules: { type: "array", items: { type: "object", additionalProperties: true } },
      },
    },
  ),
  managementTool(
    "delete_powerpages_waf_custom_rules",
    "Delete named custom WAF rules. Set confirm=true.",
    {
      mutating: true,
      destructive: true,
      properties: { ruleNames: { type: "array", minItems: 1, items: { type: "string" } } },
      required: ["ruleNames"],
    },
  ),
  managementTool(
    "start_powerpages_quick_scan",
    "Start a quick website security scan. Set confirm=true.",
    {
      mutating: true,
      properties: { lcid: { type: "string", description: "Optional language LCID" } },
    },
  ),
  managementTool(
    "start_powerpages_deep_scan",
    "Start a deep website security scan. Set confirm=true.",
    { mutating: true },
  ),
  managementTool(
    "get_powerpages_security_scan_report",
    "Get the latest completed deep security-scan report.",
  ),
  managementTool("get_powerpages_security_scan_score", "Get the latest deep security-scan score."),
  managementTool(
    "start_powerpages_website",
    "Start a stopped Power Pages website. Set confirm=true.",
    { mutating: true },
  ),
  managementTool(
    "stop_powerpages_website",
    "Stop a Power Pages website and make it unavailable. Set confirm=true.",
    { mutating: true },
  ),
  managementTool(
    "convert_powerpages_trial_to_production",
    "Convert a trial website to production and optionally enable CDN/WAF. Licensing implications apply. Set confirm=true.",
    {
      mutating: true,
      properties: {
        enableCdn: { type: "boolean" },
        enableWaf: { type: "boolean" },
        useDynamics365PortalAddOnLicense: { type: "boolean" },
      },
    },
  ),
  managementTool(
    "enable_powerpages_bootstrap_v5",
    "Stamp Bootstrap 5 enabled for a website. Set confirm=true.",
    { mutating: true },
  ),
  managementTool(
    "set_powerpages_data_model_version",
    "Set whether the website uses the enhanced data model. Set confirm=true.",
    {
      mutating: true,
      properties: { enhanced: { type: "boolean" } },
      required: ["enhanced"],
    },
  ),
  managementTool(
    "toggle_powerpages_afd_routing",
    "Enable or disable Azure Front Door traffic routing. Set confirm=true.",
    {
      mutating: true,
      properties: { enabled: { type: "boolean" } },
      required: ["enabled"],
    },
  ),
  managementTool(
    "update_powerpages_security_group",
    "Set or clear the Entra security group controlling private-site visibility. Set confirm=true.",
    {
      mutating: true,
      properties: { securityGroupId: { type: "string" }, clear: { type: "boolean" } },
    },
  ),
  managementTool(
    "update_powerpages_site_visibility",
    "Set website visibility to public or private. Set confirm=true.",
    {
      mutating: true,
      properties: { visibility: { type: "string", enum: ["public", "private"] } },
      required: ["visibility"],
    },
  ),
];

function pacPagesTool(
  name: string,
  description: string,
  properties: Record<string, JsonSchemaProperty>,
  required: string[],
  readOnly = false,
): Tool {
  return {
    name,
    description: `${description} Runs the installed pac executable without a shell and uses the active PAC authentication profile.`,
    inputSchema: {
      type: "object",
      properties: {
        ...properties,
        ...(readOnly ? {} : { confirm: confirmationProperty }),
      },
      required,
      additionalProperties: false,
    },
  };
}

const pacPathProperty = {
  type: "string",
  description: "Absolute normalized local path; symlinks and '..' traversal are rejected",
} satisfies JsonSchemaProperty;
const pacEnvironmentProperty = {
  type: "string",
  description:
    "Dataverse environment GUID or absolute HTTPS Dataverse URL; active PAC profile if omitted",
} satisfies JsonSchemaProperty;

export const pacPagesTools: Tool[] = [
  pacPagesTool(
    "pac_pages_bootstrap_migrate",
    "Migrate downloaded website HTML from Bootstrap 3 to Bootstrap 5 in place.",
    { path: pacPathProperty },
    ["path"],
  ),
  pacPagesTool(
    "pac_pages_clone",
    "Clone local Power Pages website content into a new output directory.",
    {
      path: pacPathProperty,
      outputDirectory: pacPathProperty,
      name: { type: "string" },
      overwrite: { type: "boolean" },
    },
    ["path", "outputDirectory"],
  ),
  pacPagesTool(
    "pac_pages_download",
    "Download a standard or enhanced Power Pages site from Dataverse.",
    {
      path: pacPathProperty,
      websiteId: websiteIdProperty,
      environment: pacEnvironmentProperty,
      excludeEntities: { type: "array", items: { type: "string" } },
      includeEntities: { type: "array", items: { type: "string" } },
      modelVersion: { type: "string", enum: ["Enhanced", "Standard"] },
      overwrite: { type: "boolean" },
    },
    ["path", "websiteId"],
  ),
  pacPagesTool(
    "pac_pages_download_code_site",
    "Download a Power Pages code site from Dataverse.",
    {
      path: pacPathProperty,
      websiteId: websiteIdProperty,
      environment: pacEnvironmentProperty,
      overwrite: { type: "boolean" },
    },
    ["path", "websiteId"],
  ),
  pacPagesTool(
    "pac_pages_list",
    "List websites from the current or specified PAC Dataverse environment.",
    { environment: pacEnvironmentProperty, verbose: { type: "boolean" } },
    [],
    true,
  ),
  pacPagesTool(
    "pac_pages_migrate_datamodel",
    "Start, inspect, reset, or revert a Power Pages data-model migration.",
    {
      websiteId: websiteIdProperty,
      environment: pacEnvironmentProperty,
      checkMigrationStatus: { type: "boolean" },
      mode: { type: "string", enum: ["configurationData", "configurationDataReferences", "all"] },
      portalId: { type: "string" },
      resetMigration: { type: "boolean" },
      revertToStandardDataModel: { type: "boolean" },
      siteCustomizationReportPath: pacPathProperty,
      updateDataModelVersion: { type: "boolean" },
      verbose: { type: "boolean" },
    },
    ["websiteId"],
  ),
  pacPagesTool(
    "pac_pages_upload",
    "Upload downloaded website configuration to Dataverse.",
    {
      path: pacPathProperty,
      environment: pacEnvironmentProperty,
      deploymentProfile: { type: "string" },
      forceUploadAll: { type: "boolean" },
      modelVersion: { type: "string", enum: ["Enhanced", "Standard"] },
    },
    ["path"],
  ),
  pacPagesTool(
    "pac_pages_upload_code_site",
    "Upload compiled code to a Power Pages code site.",
    {
      rootPath: pacPathProperty,
      compiledPath: pacPathProperty,
      siteName: { type: "string" },
    },
    ["rootPath"],
  ),
];

// =============================================================================
// Contexts
// =============================================================================

export interface PowerPagesDataverseContext {
  api: DataverseApi | null;
}

export interface PowerPagesMgmtContext {
  api: PowerPagesApi | null;
  adminApi: PowerPlatformAdminApi | null;
}

// =============================================================================
// Tier 1 handlers (Dataverse config)
// =============================================================================

export async function handleListPowerPagesSites(
  context: PowerPagesDataverseContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return dataverseUnavailable();
  const parsed = listSitesSchema.parse(input);
  logger.info("Executing list_powerpages_sites");

  const top = parsed.top ?? 50;
  const sites: Array<{ model: DataModel; id: string; name: string; extra: string }> = [];

  // Enhanced sites (mspp_websites)
  try {
    const enh = await context.api.queryRows(ENHANCED_SITE_SET, { select: "mspp_name", top });
    for (const row of enh.value) {
      sites.push({
        model: "enhanced",
        id: displayValue(row["mspp_websiteid"] ?? ""),
        name: displayValue(row["mspp_name"] ?? "(unnamed)"),
        extra: "",
      });
    }
  } catch (err) {
    if (!is404(err)) throw err; // mspp_websites missing -> treat as no enhanced sites
  }

  // Standard sites (adx_websites), active only
  try {
    const std = await context.api.queryRows(STANDARD_SITE_SET, {
      select: "adx_name,adx_primarydomainname",
      filter: "statecode eq 0",
      top,
    });
    for (const row of std.value) {
      const domain = row["adx_primarydomainname"]
        ? ` — ${displayValue(row["adx_primarydomainname"])}`
        : "";
      sites.push({
        model: "standard",
        id: displayValue(row["adx_websiteid"] ?? ""),
        name: displayValue(row["adx_name"] ?? "(unnamed)"),
        extra: domain,
      });
    }
  } catch (err) {
    if (!is404(err)) throw err;
  }

  if (sites.length === 0) {
    return "No Power Pages sites found in this environment's Dataverse (neither mspp_websites nor adx_websites returned rows).";
  }

  const lines = [`Power Pages sites (${sites.length}):`];
  for (const s of sites) {
    lines.push(`  [${s.model}] ${s.name}${s.extra}\n      siteId: ${s.id}`);
  }
  lines.push(
    "\nUse the siteId with list_powerpages_components (component=webpage|webrole|tablepermission|contentsnippet|webtemplate|...).",
  );
  return lines.join("\n");
}

export async function handleGetPowerPagesSite(
  context: PowerPagesDataverseContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return dataverseUnavailable();
  const parsed = getSiteSchema.parse(input);
  logger.info({ siteId: parsed.siteId }, "Executing get_powerpages_site");

  const model = await resolveModel(context.api, parsed.siteId);
  const entitySet = model === "enhanced" ? ENHANCED_SITE_SET : STANDARD_SITE_SET;
  const idColumn = model === "enhanced" ? "mspp_websiteid" : "adx_websiteid";
  // Fetch via retrieveMultiple + $filter, not retrieve-by-key: the mspp_*
  // virtual provider throws "entityMetadata" null on by-key retrieves on some
  // tenants (the probe above uses the same filter shape for the same reason).
  const rows = await context.api.queryRows(entitySet, {
    filter: `${idColumn} eq ${parsed.siteId}`,
    top: 1,
  });
  const row = rows.value[0];
  if (!row) {
    throw new ApiError(`Power Pages site ${parsed.siteId} was resolved to the ${model} model but returned no row.`, 404);
  }

  return [`Power Pages site (data model: ${model}):`, formatRow(row, "  ")].join("\n");
}

export async function handleListPowerPagesComponents(
  context: PowerPagesDataverseContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return dataverseUnavailable();
  const parsed = listComponentsSchema.parse(input);
  logger.info(
    { siteId: parsed.siteId, component: parsed.component },
    "Executing list_powerpages_components",
  );

  const model = await resolveModelOrExplicit(context.api, parsed.siteId, parsed.model);
  const target = targetFor(model, parsed.component);

  if (!target.websiteScoped && !parsed.filter) {
    throw new ApiError(
      `${parsed.component} is nested rather than directly website-scoped. Supply a parent lookup filter so the query cannot cross sites.`,
      400,
    );
  }
  const siteFilter = `${target.websiteFilterField} eq ${parsed.siteId}`;
  const filter = target.websiteScoped
    ? parsed.filter
      ? `${siteFilter} and (${parsed.filter})`
      : siteFilter
    : parsed.filter;

  const result = await context.api.queryRows(target.entitySet, {
    select: parsed.select,
    filter,
    orderBy: parsed.orderBy,
    top: parsed.top,
  });

  if (result.value.length === 0) {
    return `No ${parsed.component} components found for site ${parsed.siteId} (${model} model, ${target.entitySet}).`;
  }

  const lines = [
    `${parsed.component} components for site ${parsed.siteId} (${model} model, ${target.entitySet}, ${result.value.length} rows):`,
  ];
  for (let i = 0; i < result.value.length; i++) {
    const row = result.value[i];
    if (!row) continue;
    lines.push(`\n  Row ${i + 1}:`);
    lines.push(formatRow(row));
  }
  if (result.nextLink) lines.push("\n  [More rows available — increase top or refine filter]");
  return lines.join("\n");
}

export async function handleGetPowerPagesComponent(
  context: PowerPagesDataverseContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return dataverseUnavailable();
  const parsed = getComponentSchema.parse(input);
  logger.info({ component: parsed.component, id: parsed.id }, "Executing get_powerpages_component");

  const model = await resolveModelOrExplicit(context.api, parsed.siteId, parsed.model);
  const target = targetFor(model, parsed.component);
  const row = await context.api.getRow(target.entitySet, parsed.id, parsed.select);

  return [`${parsed.component} (${model} model, ${target.entitySet}):`, formatRow(row, "  ")].join(
    "\n",
  );
}

export async function handleCreatePowerPagesComponent(
  context: PowerPagesDataverseContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return dataverseUnavailable();
  const parsed = createComponentSchema.parse(input);
  logger.info(
    { siteId: parsed.siteId, component: parsed.component },
    "Executing create_powerpages_component",
  );

  const model = await resolveModelOrExplicit(context.api, parsed.siteId, parsed.model);
  const target = targetFor(model, parsed.component);

  const data: Record<string, unknown> = { ...parsed.data };
  // Inject the website association unless the caller already supplied the MODEL-CORRECT
  // bind. Matching on a prefix or the other model's bind would either skip injecting the
  // correct association or POST a wrong-collection bind to the wrong table.
  const hasBinding = Object.prototype.hasOwnProperty.call(data, target.websiteBindProp);
  if (target.websiteScoped && !hasBinding) {
    data[target.websiteBindProp] = `${target.websiteBindCollection}(${parsed.siteId})`;
  }

  const row = await context.api.createRow(target.entitySet, data);
  return [
    `Created ${parsed.component} in ${target.entitySet} (${model} model):`,
    formatRow(row, "  "),
  ].join("\n");
}

export async function handleUpdatePowerPagesComponent(
  context: PowerPagesDataverseContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return dataverseUnavailable();
  const parsed = updateComponentSchema.parse(input);
  logger.info(
    { component: parsed.component, id: parsed.id },
    "Executing update_powerpages_component",
  );

  const model = await resolveMutationModel(context.api, parsed.siteId, parsed.model);
  const target = targetFor(model, parsed.component);
  await assertComponentBelongsToSite(
    context.api,
    parsed.component,
    parsed.id,
    parsed.siteId,
    model,
  );
  await context.api.updateRow(target.entitySet, parsed.id, parsed.data);

  return `Updated ${parsed.component} ${parsed.id} in ${target.entitySet} (${model} model). Columns: ${Object.keys(parsed.data).join(", ")}`;
}

export async function handleDeletePowerPagesComponent(
  context: PowerPagesDataverseContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return dataverseUnavailable();
  const parsed = deleteComponentSchema.parse(input);

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Deletion not confirmed. Set confirm=true to delete ${parsed.component} ${parsed.id}. This cannot be undone.`);
  }

  const model = await resolveMutationModel(context.api, parsed.siteId, parsed.model);
  const target = targetFor(model, parsed.component);
  await assertComponentBelongsToSite(
    context.api,
    parsed.component,
    parsed.id,
    parsed.siteId,
    model,
  );
  logger.info(
    { component: parsed.component, id: parsed.id },
    "Executing delete_powerpages_component",
  );
  await context.api.deleteRow(target.entitySet, parsed.id);

  return `Deleted ${parsed.component} ${parsed.id} from ${target.entitySet} (${model} model).`;
}

function lookupValue(row: DataverseRow, name: string): string | undefined {
  const wanted = name.toLowerCase();
  const entry = Object.entries(row).find(([key]) => key.toLowerCase() === wanted);
  return entry?.[1] === undefined || entry[1] === null ? undefined : displayValue(entry[1]);
}

async function assertComponentBelongsToSite(
  api: DataverseApi,
  component: string,
  recordId: string,
  siteId: string,
  model: DataModel,
  visited = new Set<string>(),
): Promise<void> {
  const visitKey = `${component}:${recordId}`.toLowerCase();
  if (visited.has(visitKey)) {
    throw new ApiError(`Power Pages component ${recordId} has a cyclic parent chain`, 500);
  }
  visited.add(visitKey);

  const target = targetFor(model, component);
  const row = await api.getRow(target.entitySet, recordId);
  if (target.websiteScoped) {
    const websiteId = lookupValue(row, target.websiteFilterField);
    if (!websiteId || websiteId.toLowerCase() !== siteId.toLowerCase()) {
      throw new ApiError(`${recordId} does not belong to Power Pages site ${siteId}`, 400);
    }
    return;
  }

  const parent = COMPONENT_MAP[component]?.parent;
  if (!parent) {
    throw new ApiError(`Cannot establish website membership for nested ${component}`, 500);
  }
  const parentLookup = model === "enhanced" ? parent.msppLookupField : parent.adxLookupField;
  const parentId = lookupValue(row, parentLookup);
  if (!parentId) {
    throw new ApiError(`${recordId} does not belong to Power Pages site ${siteId}`, 400);
  }
  await assertComponentBelongsToSite(api, parent.component, parentId, siteId, model, visited);
}

async function assertRecordBelongsToSite(
  api: DataverseApi,
  entitySet: string,
  recordId: string,
  siteId: string,
  model: DataModel,
): Promise<void> {
  const row = await api.getRow(entitySet, recordId);
  const lookup = lookupValue(
    row,
    model === "enhanced" ? "_mspp_websiteid_value" : "_adx_websiteid_value",
  );
  if (!lookup || lookup.toLowerCase() !== siteId.toLowerCase()) {
    throw new ApiError(`${recordId} does not belong to Power Pages site ${siteId}`, 400);
  }
}

export async function handleManagePowerPagesRelationship(
  context: PowerPagesDataverseContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return dataverseUnavailable();
  const parsed = relationshipSchema.parse(input);
  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Relationship change not confirmed. Set confirm=true to ${parsed.action} ${parsed.relationship}; this can change site authorization.`);
  }

  const model = await resolveModelOrExplicit(context.api, parsed.siteId, parsed.model);
  const mapping = RELATIONSHIP_MAP[parsed.relationship];
  const source = targetFor(model, mapping.sourceComponent);
  const webRole = targetFor(model, "webrole");
  await assertRecordBelongsToSite(
    context.api,
    source.entitySet,
    parsed.sourceId,
    parsed.siteId,
    model,
  );
  await assertRecordBelongsToSite(
    context.api,
    webRole.entitySet,
    parsed.webRoleId,
    parsed.siteId,
    model,
  );
  const relationshipName = model === "enhanced" ? mapping.mspp : mapping.adx;

  if (parsed.action === "associate") {
    await context.api.associateReference(
      source.entitySet,
      parsed.sourceId,
      relationshipName,
      webRole.entitySet,
      parsed.webRoleId,
    );
  } else {
    await context.api.disassociateReference(
      source.entitySet,
      parsed.sourceId,
      relationshipName,
      parsed.webRoleId,
    );
  }
  return `${parsed.action === "associate" ? "Associated" : "Disassociated"} ${parsed.relationship} (${model} model).`;
}

async function readSafeLocalFile(filePath: string, maxBytes: number): Promise<Buffer> {
  if (!isAbsolute(filePath) || resolve(filePath) !== filePath) {
    throw new ApiError("filePath must be an absolute, normalized path without '..' traversal", 400);
  }
  const metadata = await lstat(filePath);
  if (metadata.isSymbolicLink() || !metadata.isFile()) {
    throw new ApiError("filePath must reference a regular file, not a symlink or directory", 400);
  }
  const canonical = await realpath(filePath);
  if (metadata.size > maxBytes) {
    throw new ApiError(
      `Local file exceeds the ${Math.floor(maxBytes / 1024 / 1024)} MiB safety limit`,
      413,
    );
  }
  await access(canonical, fsConstants.R_OK);
  return readFile(canonical);
}

export async function handleUploadPowerPagesWebFileContent(
  context: PowerPagesDataverseContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return dataverseUnavailable();
  const parsed = uploadWebFileContentSchema.parse(input);
  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Upload not confirmed. Set confirm=true to attach ${parsed.filePath} to web file ${parsed.webFileId}.`);
  }
  const model = await resolveModelOrExplicit(context.api, parsed.siteId, parsed.model);
  if (model === "enhanced") {
    throw new ApiError(
      "Direct note serialization for enhanced-model virtual web files is not documented. Use pac_pages_upload for enhanced sites.",
      400,
    );
  }
  await assertRecordBelongsToSite(
    context.api,
    COMPONENT_MAP.webfile!.adx,
    parsed.webFileId,
    parsed.siteId,
    model,
  );
  const fileName = parsed.fileName ?? basename(parsed.filePath);
  if (fileName.includes("/") || fileName.includes("\\")) {
    throw new ApiError("fileName must not contain path separators", 400);
  }
  const bytes = await readSafeLocalFile(parsed.filePath, 15 * 1024 * 1024);
  const note = await context.api.createRow("annotations", {
    subject: `Power Pages web file: ${fileName}`,
    filename: fileName,
    mimetype: parsed.mimeType ?? "application/octet-stream",
    documentbody: bytes.toString("base64"),
    isdocument: true,
    "objectid_adx_webfile@odata.bind": `/adx_webfiles(${parsed.webFileId})`,
  });
  return `Uploaded ${bytes.length} bytes as the newest note for web file ${parsed.webFileId}. Annotation: ${displayValue(note.annotationid ?? "created")}`;
}

// =============================================================================
// Tier 2 handlers (management)
// =============================================================================

function mgmtUnavailable(): string {
  return `Power Pages management API is not configured.

These tools call https://api.powerplatform.com which needs the "Power Platform API"
delegated permission on the app registration. If you see auth errors, re-run
'powerautomate-mcp --setup' and ensure that permission is consented.`;
}

function formatWebsite(w: Record<string, unknown>): string {
  const get = (k: string): string | undefined => {
    const v = w[k];
    return v === null || v === undefined ? undefined : displayValue(v);
  };
  const name = get("name") ?? "(unnamed)";
  const id = get("id") ?? "?";
  const parts = [`  ${name} (${id})`];
  const status = get("status");
  const url = get("websiteUrl");
  const type = get("type");
  const model = get("dataModel");
  if (status) parts.push(`      status: ${status}`);
  if (url) parts.push(`      url: ${url}`);
  if (type) parts.push(`      type: ${type}`);
  if (model) parts.push(`      dataModel: ${model}`);
  return parts.join("\n");
}

export async function handleListPowerPagesWebsites(
  context: PowerPagesMgmtContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return mgmtUnavailable();
  const parsed = listWebsitesSchema.parse(input);
  logger.info({ environment: parsed.environment }, "Executing list_powerpages_websites");

  const sites = await context.api.listWebsites(parsed.environment);
  if (sites.length === 0) return "No Power Pages websites found in this environment.";

  const lines = [`Power Pages websites (${sites.length}):`];
  for (const s of sites) lines.push(formatWebsite(s));
  return lines.join("\n");
}

export async function handleGetPowerPagesWebsite(
  context: PowerPagesMgmtContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return mgmtUnavailable();
  const parsed = getWebsiteSchema.parse(input);
  logger.info({ websiteId: parsed.websiteId }, "Executing get_powerpages_website");

  const site = await context.api.getWebsite(parsed.websiteId, parsed.environment);
  return ["Power Pages website:", formatWebsite(site)].join("\n");
}

export async function handleCreatePowerPagesWebsite(
  context: PowerPagesMgmtContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return mgmtUnavailable();
  const parsed = createWebsiteSchema.parse(input);

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Provisioning not confirmed. This creates a new hosted Power Pages site "${parsed.name}" at subdomain "${parsed.subdomain}". Set confirm=true to proceed.`);
  }

  logger.info(
    { name: parsed.name, subdomain: parsed.subdomain },
    "Executing create_powerpages_website",
  );

  // Resolve the Dataverse org id from the environment if not supplied.
  let dataverseOrganizationId = parsed.dataverseOrganizationId;
  if (!dataverseOrganizationId) {
    if (!context.adminApi) {
      return "dataverseOrganizationId is required and could not be auto-resolved (admin API unavailable). Pass dataverseOrganizationId explicitly.";
    }
    const env = await context.adminApi.getEnvironment(parsed.environment);
    dataverseOrganizationId = env.properties?.linkedEnvironmentMetadata?.resourceId;
    if (!dataverseOrganizationId) {
      return "Could not resolve the Dataverse organization id from the environment. The environment may not have Dataverse provisioned. Pass dataverseOrganizationId explicitly.";
    }
  }

  const result = await context.api.createWebsite(
    {
      name: parsed.name,
      subdomain: parsed.subdomain,
      templateName: parsed.templateName,
      selectedBaseLanguage: parsed.languageLcid,
      dataverseOrganizationId,
      websiteRecordId: parsed.websiteRecordId,
    },
    parsed.environment,
  );

  const lines = [
    `Power Pages website provisioning started for "${parsed.name}" (subdomain: ${parsed.subdomain}).`,
    `HTTP ${result.statusCode} — provisioning is asynchronous and takes several minutes.`,
  ];
  if (result.operationLocation) {
    lines.push(`Track status at: ${result.operationLocation}`);
    if (parsed.waitForCompletion) {
      const status = await context.api.waitForOperation(result.operationLocation, {
        timeoutMs: (parsed.timeoutSeconds ?? 60) * 1_000,
      });
      lines.push(`Final status:\n${JSON.stringify(status, null, 2)}`);
    }
  }
  lines.push("Use list_powerpages_websites to check status (look for status=OperationComplete).");
  return lines.join("\n");
}

export async function handleDeletePowerPagesWebsite(
  context: PowerPagesMgmtContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return mgmtUnavailable();
  const parsed = deleteWebsiteSchema.parse(input);

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Deletion not confirmed. Set confirm=true to delete website ${parsed.websiteId}. This cannot be undone.`);
  }

  logger.info({ websiteId: parsed.websiteId }, "Executing delete_powerpages_website");
  const result = await context.api.deleteWebsite(parsed.websiteId, parsed.environment);
  const lines = [
    `Deletion of Power Pages website ${parsed.websiteId} requested (HTTP ${result.statusCode}).`,
  ];
  if (result.operationLocation) {
    lines.push(`Track status at: ${result.operationLocation}`);
    if (parsed.waitForCompletion) {
      const status = await context.api.waitForOperation(result.operationLocation, {
        timeoutMs: (parsed.timeoutSeconds ?? 60) * 1_000,
      });
      lines.push(`Final status:\n${JSON.stringify(status, null, 2)}`);
    }
  } else {
    lines.push("Deletion is processing; use list_powerpages_websites to confirm it's gone.");
  }
  return lines.join("\n");
}

export async function handleRestartPowerPagesWebsite(
  context: PowerPagesMgmtContext,
  input: unknown,
): Promise<string> {
  if (!context.api) return mgmtUnavailable();
  const parsed = restartWebsiteSchema.parse(input);
  logger.info({ websiteId: parsed.websiteId }, "Executing restart_powerpages_website");

  await context.api.restartWebsite(parsed.websiteId, parsed.environment);
  return `Restarted Power Pages website ${parsed.websiteId}. Config changes should now be live.`;
}

type PowerPagesManagementHandler = (
  context: PowerPagesMgmtContext,
  input: unknown,
) => Promise<string>;

function requireConfirmation(value: { confirm?: boolean }, operation: string): void {
  if (!value.confirm) {
    throw new ApiError(`${operation} not confirmed. Set confirm=true to proceed.`, 400);
  }
}

function formatManagementValue(label: string, value: unknown): string {
  return `${label}:\n${JSON.stringify(value, null, 2)}`;
}

async function formatManagementMutation(
  api: PowerPagesApi,
  label: string,
  result: PowerPagesOperationResult,
  options: { waitForCompletion?: boolean; timeoutSeconds?: number },
): Promise<string> {
  const lines = [`${label} accepted (HTTP ${result.statusCode}).`];
  if (result.operationLocation) {
    lines.push(`Operation-Location: ${result.operationLocation}`);
    if (options.waitForCompletion) {
      const status = await api.waitForOperation(result.operationLocation, {
        timeoutMs: (options.timeoutSeconds ?? 60) * 1_000,
      });
      lines.push(`Final status:\n${JSON.stringify(status, null, 2)}`);
    }
  } else if (options.waitForCompletion) {
    lines.push("The service completed synchronously; no Operation-Location was returned.");
  }
  return lines.join("\n");
}

function withManagementApi(
  handler: (api: PowerPagesApi, input: unknown) => Promise<string>,
): PowerPagesManagementHandler {
  return async (context, input) => {
    if (!context.api) return mgmtUnavailable();
    return handler(context.api, input);
  };
}

export const powerPagesManagementHandlers: Record<string, PowerPagesManagementHandler> = {
  get_powerpages_operation_status: withManagementApi(async (api, input) => {
    const parsed = operationStatusSchema.parse(input);
    const status = parsed.waitForCompletion
      ? await api.waitForOperation(parsed.operationLocation, {
          timeoutMs: (parsed.timeoutSeconds ?? 60) * 1_000,
        })
      : await api.getOperationStatus(parsed.operationLocation);
    return formatManagementValue("Power Pages operation", status);
  }),
  get_powerpages_allowed_ip_addresses: withManagementApi(async (api, input) => {
    const parsed = websiteManagementSchema.parse(input);
    return formatManagementValue(
      "Allowed IP addresses",
      await api.getAllowedIpAddresses(parsed.websiteId, parsed.environment),
    );
  }),
  add_powerpages_allowed_ip_addresses: withManagementApi(async (api, input) => {
    const parsed = ipMutationSchema.parse(input);
    requireConfirmation(parsed, "Adding allowed IP addresses");
    const result = await api.addAllowedIpAddresses(
      parsed.websiteId,
      parsed.ipAddresses.map(parseIpAddress),
      parsed.environment,
    );
    return formatManagementMutation(api, "Allowed IP address update", result, parsed);
  }),
  remove_powerpages_allowed_ip_addresses: withManagementApi(async (api, input) => {
    const parsed = ipMutationSchema.parse(input);
    requireConfirmation(parsed, "Removing allowed IP addresses");
    const result = await api.removeAllowedIpAddresses(
      parsed.websiteId,
      parsed.ipAddresses.map(parseIpAddress),
      parsed.environment,
    );
    return formatManagementMutation(api, "Allowed IP address removal", result, parsed);
  }),
  list_powerpages_custom_domains: withManagementApi(async (api, input) => {
    const parsed = websiteManagementSchema.parse(input);
    return formatManagementValue(
      "Custom domains",
      await api.listCustomDomains(parsed.websiteId, parsed.environment),
    );
  }),
  create_powerpages_custom_domain: withManagementApi(async (api, input) => {
    const parsed = customDomainCreateSchema.parse(input);
    requireConfirmation(parsed, "Creating a custom domain");
    const result = await api.createCustomDomain(
      parsed.websiteId,
      parsed.customDomain,
      parsed.environment,
    );
    return formatManagementMutation(api, "Custom-domain creation", result, parsed);
  }),
  delete_powerpages_custom_domain: withManagementApi(async (api, input) => {
    const parsed = hostnameSchema.parse(input);
    requireConfirmation(parsed, "Deleting a custom domain");
    const result = await api.deleteCustomDomain(
      parsed.websiteId,
      parsed.hostName,
      parsed.environment,
    );
    return formatManagementMutation(api, "Custom-domain deletion", result, parsed);
  }),
  list_powerpages_certificates: withManagementApi(async (api, input) => {
    const parsed = certificateListSchema.parse(input);
    return formatManagementValue(
      "Certificates",
      await api.getCertificates(parsed.websiteId, parsed.certType, parsed.environment),
    );
  }),
  upload_powerpages_certificate: withManagementApi(async (api, input) => {
    const parsed = certificateUploadSchema.parse(input);
    requireConfirmation(parsed, "Uploading a certificate");
    if (!parsed.certificatePath.toLowerCase().endsWith(".pfx")) {
      throw new ApiError("certificatePath must point to a .pfx file", 400);
    }
    const file = await readSafeLocalFile(parsed.certificatePath, 16 * 1024 * 1024);
    let password = "";
    if (parsed.passwordEnvironmentVariable) {
      const environmentPassword = process.env[parsed.passwordEnvironmentVariable];
      if (environmentPassword === undefined) {
        throw new ApiError(
          `Certificate password environment variable ${parsed.passwordEnvironmentVariable} is not set`,
          400,
        );
      }
      password = environmentPassword;
      if (password.length > 4_096) throw new ApiError("Certificate password is too long", 400);
    }
    const result = await api.uploadCertificate(
      parsed.websiteId,
      {
        certType: parsed.certType,
        file,
        fileName: basename(parsed.certificatePath),
        password,
      },
      parsed.environment,
    );
    return formatManagementMutation(api, "Certificate upload", result, parsed);
  }),
  delete_powerpages_certificate: withManagementApi(async (api, input) => {
    const parsed = certificateDeleteSchema.parse(input);
    requireConfirmation(parsed, "Deleting a certificate");
    const result = await api.deleteCertificate(
      parsed.websiteId,
      parsed.thumbprint,
      parsed.certType,
      parsed.environment,
    );
    return formatManagementMutation(api, "Certificate deletion", result, parsed);
  }),
  list_powerpages_ssl_bindings: withManagementApi(async (api, input) => {
    const parsed = sslBindingListSchema.parse(input);
    return formatManagementValue(
      "SSL bindings",
      await api.listSslBindings(parsed.websiteId, parsed.hostName, parsed.environment),
    );
  }),
  add_powerpages_ssl_binding: withManagementApi(async (api, input) => {
    const parsed = sslBindingSchema.parse(input);
    requireConfirmation(parsed, "Adding an SSL binding");
    const result = await api.addSslBinding(
      parsed.websiteId,
      parsed.thumbprint,
      parsed.hostName,
      parsed.environment,
    );
    return formatManagementMutation(api, "SSL binding creation", result, parsed);
  }),
  delete_powerpages_ssl_binding: withManagementApi(async (api, input) => {
    const parsed = sslBindingSchema.parse(input);
    requireConfirmation(parsed, "Deleting an SSL binding");
    const result = await api.deleteSslBinding(
      parsed.websiteId,
      parsed.hostName,
      parsed.thumbprint,
      parsed.environment,
    );
    return formatManagementMutation(api, "SSL binding deletion", result, parsed);
  }),
  get_powerpages_waf_status: withManagementApi(async (api, input) => {
    const parsed = websiteManagementSchema.parse(input);
    return formatManagementValue(
      "WAF status",
      await api.getWafStatus(parsed.websiteId, parsed.environment),
    );
  }),
  get_powerpages_waf_rules: withManagementApi(async (api, input) => {
    const parsed = wafGetRulesSchema.parse(input);
    return formatManagementValue(
      "WAF rules",
      await api.getWafRules(parsed.websiteId, parsed.ruleType, parsed.environment),
    );
  }),
  enable_powerpages_waf: withManagementApi(async (api, input) => {
    const parsed = confirmedWebsiteManagementSchema.parse(input);
    requireConfirmation(parsed, "Enabling WAF");
    return formatManagementMutation(
      api,
      "WAF enablement",
      await api.enableWaf(parsed.websiteId, parsed.environment),
      parsed,
    );
  }),
  disable_powerpages_waf: withManagementApi(async (api, input) => {
    const parsed = confirmedWebsiteManagementSchema.parse(input);
    requireConfirmation(parsed, "Disabling WAF");
    return formatManagementMutation(
      api,
      "WAF disablement",
      await api.disableWaf(parsed.websiteId, parsed.environment),
      parsed,
    );
  }),
  create_powerpages_waf_rules: withManagementApi(async (api, input) => {
    const parsed = wafRulesMutationSchema.parse(input);
    requireConfirmation(parsed, "Creating WAF rules");
    const rules = {
      customRules: parsed.customRules ?? [],
      managedRules: parsed.managedRules ?? [],
    } as unknown as PowerPagesWafRules;
    return formatManagementMutation(
      api,
      "WAF rule update",
      await api.createWafRules(parsed.websiteId, rules, parsed.environment),
      parsed,
    );
  }),
  delete_powerpages_waf_custom_rules: withManagementApi(async (api, input) => {
    const parsed = wafDeleteRulesSchema.parse(input);
    requireConfirmation(parsed, "Deleting custom WAF rules");
    return formatManagementMutation(
      api,
      "WAF custom-rule deletion",
      await api.deleteWafCustomRules(parsed.websiteId, parsed.ruleNames, parsed.environment),
      parsed,
    );
  }),
  start_powerpages_quick_scan: withManagementApi(async (api, input) => {
    const parsed = quickScanSchema.parse(input);
    requireConfirmation(parsed, "Starting a quick security scan");
    return formatManagementMutation(
      api,
      "Quick security scan",
      await api.startQuickScan(parsed.websiteId, parsed.lcid, parsed.environment),
      parsed,
    );
  }),
  start_powerpages_deep_scan: withManagementApi(async (api, input) => {
    const parsed = confirmedWebsiteManagementSchema.parse(input);
    requireConfirmation(parsed, "Starting a deep security scan");
    return formatManagementMutation(
      api,
      "Deep security scan",
      await api.startDeepScan(parsed.websiteId, parsed.environment),
      parsed,
    );
  }),
  get_powerpages_security_scan_report: withManagementApi(async (api, input) => {
    const parsed = websiteManagementSchema.parse(input);
    return formatManagementValue(
      "Security scan report",
      await api.getSecurityScanReport(parsed.websiteId, parsed.environment),
    );
  }),
  get_powerpages_security_scan_score: withManagementApi(async (api, input) => {
    const parsed = websiteManagementSchema.parse(input);
    return formatManagementValue(
      "Security scan score",
      await api.getSecurityScanScore(parsed.websiteId, parsed.environment),
    );
  }),
  start_powerpages_website: withManagementApi(async (api, input) => {
    const parsed = confirmedWebsiteManagementSchema.parse(input);
    requireConfirmation(parsed, "Starting the website");
    return formatManagementMutation(
      api,
      "Website start",
      await api.startWebsite(parsed.websiteId, parsed.environment),
      parsed,
    );
  }),
  stop_powerpages_website: withManagementApi(async (api, input) => {
    const parsed = confirmedWebsiteManagementSchema.parse(input);
    requireConfirmation(parsed, "Stopping the website");
    return formatManagementMutation(
      api,
      "Website stop",
      await api.stopWebsite(parsed.websiteId, parsed.environment),
      parsed,
    );
  }),
  convert_powerpages_trial_to_production: withManagementApi(async (api, input) => {
    const parsed = trialConversionSchema.parse(input);
    requireConfirmation(parsed, "Converting the trial website to production");
    const result = await api.convertTrialToProduction(
      parsed.websiteId,
      {
        EnableCDN: parsed.enableCdn,
        EnableWAF: parsed.enableWaf,
        UseDynamics365PortalAddOnLicense: parsed.useDynamics365PortalAddOnLicense,
      },
      parsed.environment,
    );
    return formatManagementMutation(api, "Trial-to-production conversion", result, parsed);
  }),
  enable_powerpages_bootstrap_v5: withManagementApi(async (api, input) => {
    const parsed = confirmedWebsiteManagementSchema.parse(input);
    requireConfirmation(parsed, "Enabling Bootstrap 5");
    return formatManagementMutation(
      api,
      "Bootstrap 5 enablement",
      await api.setBootstrapV5Enabled(parsed.websiteId, parsed.environment),
      parsed,
    );
  }),
  set_powerpages_data_model_version: withManagementApi(async (api, input) => {
    const parsed = setDataModelSchema.parse(input);
    requireConfirmation(parsed, "Changing the website data-model version");
    return formatManagementMutation(
      api,
      "Data-model update",
      await api.setDataModelVersion(parsed.websiteId, parsed.enhanced, parsed.environment),
      parsed,
    );
  }),
  toggle_powerpages_afd_routing: withManagementApi(async (api, input) => {
    const parsed = toggleAfdSchema.parse(input);
    requireConfirmation(parsed, "Changing AFD traffic routing");
    return formatManagementMutation(
      api,
      "AFD routing update",
      await api.toggleAfdTrafficRouting(parsed.websiteId, parsed.enabled, parsed.environment),
      parsed,
    );
  }),
  update_powerpages_security_group: withManagementApi(async (api, input) => {
    const parsed = securityGroupSchema.parse(input);
    requireConfirmation(parsed, "Changing the website security group");
    return formatManagementMutation(
      api,
      "Security-group update",
      await api.updateSecurityGroup(
        parsed.websiteId,
        parsed.clear ? undefined : parsed.securityGroupId,
        parsed.environment,
      ),
      parsed,
    );
  }),
  update_powerpages_site_visibility: withManagementApi(async (api, input) => {
    const parsed = visibilitySchema.parse(input);
    requireConfirmation(parsed, "Changing site visibility");
    return formatManagementMutation(
      api,
      "Site-visibility update",
      await api.updateSiteVisibility(parsed.websiteId, parsed.visibility, parsed.environment),
      parsed,
    );
  }),
};

async function validatePacPath(
  value: string,
  mode: "existing-directory" | "output-path",
): Promise<string> {
  if (!isAbsolute(value) || resolve(value) !== value || value.includes("\0")) {
    throw new ApiError("PAC path must be absolute and normalized without '..' traversal", 400);
  }
  if (mode === "existing-directory") {
    const metadata = await lstat(value);
    if (metadata.isSymbolicLink() || !metadata.isDirectory()) {
      throw new ApiError("PAC input path must be a real directory, not a symlink", 400);
    }
    return realpath(value);
  }

  try {
    const metadata = await lstat(value);
    if (metadata.isSymbolicLink()) throw new ApiError("PAC output path must not be a symlink", 400);
    return await realpath(value);
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error;
    const parent = dirname(value);
    const parentMetadata = await lstat(parent);
    if (parentMetadata.isSymbolicLink() || !parentMetadata.isDirectory()) {
      throw new ApiError("PAC output parent must be a real directory", 400);
    }
    return join(await realpath(parent), basename(value));
  }
}

function validatePacEnvironment(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return value;
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new ApiError("PAC environment must be a GUID or absolute HTTPS Dataverse URL", 400);
  }
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.pathname !== "/" ||
    url.search ||
    url.hash ||
    !/(?:\.crm\d*\.dynamics\.com|\.dynamics\.com)$/i.test(url.hostname)
  ) {
    throw new ApiError("PAC environment URL must be an HTTPS Dataverse origin", 400);
  }
  return url.origin;
}

async function runPacPages(args: string[]): Promise<string> {
  try {
    const result = await execFileAsync("pac", ["pages", ...args], {
      encoding: "utf8",
      timeout: 15 * 60_000,
      maxBuffer: 2 * 1024 * 1024,
      windowsHide: true,
    });
    const stdout = String(result.stdout ?? "").trim();
    const stderr = String(result.stderr ?? "").trim();
    return [stdout, stderr].filter(Boolean).join("\n") || "PAC command completed successfully.";
  } catch (error) {
    const err = error as NodeJS.ErrnoException & { stdout?: string; stderr?: string };
    if (err.code === "ENOENT") {
      throw new ApiError(
        "Power Platform CLI ('pac') is not installed or is not on PATH. Install PAC CLI and authenticate with 'pac auth create'.",
        503,
      );
    }
    const details = String(err.stderr ?? err.stdout ?? err.message).slice(0, 8_000);
    throw new ApiError("pac pages command failed", 500, details);
  }
}

function appendPacEnvironment(args: string[], environment: string | undefined): void {
  const safeEnvironment = validatePacEnvironment(environment);
  if (safeEnvironment) args.push("--environment", safeEnvironment);
}

export const pacPagesHandlers: Record<string, PowerPagesManagementHandler> = {
  pac_pages_bootstrap_migrate: async (_context, input) => {
    const parsed = pacBootstrapSchema.parse(input);
    requireConfirmation(parsed, "Bootstrap migration");
    return runPacPages([
      "bootstrap-migrate",
      "--path",
      await validatePacPath(parsed.path, "existing-directory"),
    ]);
  },
  pac_pages_clone: async (_context, input) => {
    const parsed = pacCloneSchema.parse(input);
    requireConfirmation(parsed, "Power Pages clone");
    const args = [
      "clone",
      "--path",
      await validatePacPath(parsed.path, "existing-directory"),
      "--outputDirectory",
      await validatePacPath(parsed.outputDirectory, "output-path"),
    ];
    if (parsed.name) args.push("--name", parsed.name);
    if (parsed.overwrite) args.push("--overwrite");
    return runPacPages(args);
  },
  pac_pages_download: async (_context, input) => {
    const parsed = pacDownloadSchema.parse(input);
    requireConfirmation(parsed, "Power Pages download");
    if (parsed.includeEntities && parsed.excludeEntities) {
      throw new ApiError("includeEntities and excludeEntities are mutually exclusive", 400);
    }
    const args = [
      "download",
      "--path",
      await validatePacPath(parsed.path, "output-path"),
      "--webSiteId",
      parsed.websiteId,
    ];
    appendPacEnvironment(args, parsed.environment);
    if (parsed.excludeEntities) args.push("--excludeEntities", parsed.excludeEntities.join(","));
    if (parsed.includeEntities) args.push("--includeEntities", parsed.includeEntities.join(","));
    if (parsed.modelVersion) args.push("--modelVersion", parsed.modelVersion);
    if (parsed.overwrite) args.push("--overwrite");
    return runPacPages(args);
  },
  pac_pages_download_code_site: async (_context, input) => {
    const parsed = pacCodeDownloadSchema.parse(input);
    requireConfirmation(parsed, "Power Pages code-site download");
    const args = [
      "download-code-site",
      "--path",
      await validatePacPath(parsed.path, "output-path"),
      "--webSiteId",
      parsed.websiteId,
    ];
    appendPacEnvironment(args, parsed.environment);
    if (parsed.overwrite) args.push("--overwrite");
    return runPacPages(args);
  },
  pac_pages_list: async (_context, input) => {
    const parsed = pacListSchema.parse(input);
    const args = ["list"];
    appendPacEnvironment(args, parsed.environment);
    if (parsed.verbose) args.push("--verbose");
    return runPacPages(args);
  },
  pac_pages_migrate_datamodel: async (_context, input) => {
    const parsed = pacMigrateSchema.parse(input);
    requireConfirmation(parsed, "Power Pages data-model migration");
    const args = ["migrate-datamodel", "--webSiteId", parsed.websiteId];
    appendPacEnvironment(args, parsed.environment);
    if (parsed.checkMigrationStatus) args.push("--checkMigrationStatus");
    if (parsed.mode) args.push("--mode", parsed.mode);
    if (parsed.portalId) args.push("--portalId", parsed.portalId);
    if (parsed.resetMigration) args.push("--resetMigration");
    if (parsed.revertToStandardDataModel) args.push("--revertToStandardDataModel");
    if (parsed.siteCustomizationReportPath) {
      args.push(
        "--siteCustomizationReportPath",
        await validatePacPath(parsed.siteCustomizationReportPath, "output-path"),
      );
    }
    if (parsed.updateDataModelVersion) args.push("--updateDataModelVersion");
    if (parsed.verbose) args.push("--verbose");
    return runPacPages(args);
  },
  pac_pages_upload: async (_context, input) => {
    const parsed = pacUploadSchema.parse(input);
    requireConfirmation(parsed, "Power Pages upload");
    const args = ["upload", "--path", await validatePacPath(parsed.path, "existing-directory")];
    appendPacEnvironment(args, parsed.environment);
    if (parsed.deploymentProfile) args.push("--deploymentProfile", parsed.deploymentProfile);
    if (parsed.forceUploadAll) args.push("--forceUploadAll");
    if (parsed.modelVersion) args.push("--modelVersion", parsed.modelVersion);
    return runPacPages(args);
  },
  pac_pages_upload_code_site: async (_context, input) => {
    const parsed = pacCodeUploadSchema.parse(input);
    requireConfirmation(parsed, "Power Pages code-site upload");
    const args = [
      "upload-code-site",
      "--rootPath",
      await validatePacPath(parsed.rootPath, "existing-directory"),
    ];
    if (parsed.compiledPath) {
      args.push("--compiledPath", await validatePacPath(parsed.compiledPath, "existing-directory"));
    }
    if (parsed.siteName) args.push("--siteName", parsed.siteName);
    return runPacPages(args);
  },
};

// =============================================================================
// Tool array
// =============================================================================

export const powerPagesTools: Tool[] = [
  // Tier 1 — Dataverse config
  listPowerPagesSitesTool,
  getPowerPagesSiteTool,
  listPowerPagesComponentsTool,
  getPowerPagesComponentTool,
  createPowerPagesComponentTool,
  updatePowerPagesComponentTool,
  deletePowerPagesComponentTool,
  managePowerPagesRelationshipTool,
  uploadPowerPagesWebFileContentTool,
  // Tier 2 — management
  listPowerPagesWebsitesTool,
  getPowerPagesWebsiteTool,
  createPowerPagesWebsiteTool,
  deletePowerPagesWebsiteTool,
  restartPowerPagesWebsiteTool,
  ...additionalPowerPagesManagementTools,
  ...pacPagesTools,
];
